import React from 'react';
import clsx from 'clsx';
import { Accordion, AccordionItem, CountBadge, Icon } from '@brighterionai/br-ui';
import { SIDE_PANEL_CONTENT_TYPE } from '@brighterion/utils/constants';
import useGetSidePanelContentData from '../../../hooks/useGetSidePanelContentData';

import styles from './SidePanelAccordionItem.css';
import SidePanelAccordionItemChildren from './SidePanelAccordionItemChildren';

type SidePanelAccordionItemProps = {
    itemDisplayName: string;
    itemTitle: SIDE_PANEL_CONTENT_TYPE;
    noDataMessage?: string;
};

const SidePanelAccordionItem = ({ itemDisplayName, itemTitle, noDataMessage }: SidePanelAccordionItemProps) => {
    const getContentData = useGetSidePanelContentData();
    const itemData = React.useMemo(() => {
        return getContentData(itemTitle);
    }, [getContentData, itemTitle]);
    const itemCount = itemData?.length;

    const displayAccordionHeader = (handleOnClick: () => void, handleKeyDown: () => void, expanded: boolean) => {
        return (
            <div
                className={clsx(styles.headerContainer, expanded ? styles.expanded : undefined)}
                tabIndex={0}
                onClick={handleOnClick}
                onKeyDown={handleKeyDown}
                role='button'
                data-testid='accordion-header-container'
            >
                <div className={styles.leftContainer}>
                    <h6 data-testid={`${itemTitle}-items`} className={styles.title}>
                        {itemDisplayName}
                    </h6>
                    <CountBadge
                        type='neutral-light'
                        data-testid={`${itemTitle}-itemsCount`}
                        containerClassName={styles.count}
                    >
                        {itemCount.toString() ?? '0'}
                    </CountBadge>
                </div>
                <Icon
                    className={styles.icon}
                    size='md'
                    data-testid='accordion-header-arrow-icon'
                    name={expanded ? 'arrow-up-1' : 'arrow-down-1'}
                />
            </div>
        );
    };

    return (
        <Accordion className={styles.accordion} noAutoCollapse dataTestId={`${itemTitle}-accordion`}>
            <AccordionItem
                name={itemTitle}
                childContainerClassName={clsx((itemCount > 0 || noDataMessage) && styles.childContainer)}
                header={({ handleOnClick, handleKeyDown, expanded }) => {
                    return displayAccordionHeader(handleOnClick, handleKeyDown, expanded);
                }}
            >
                <SidePanelAccordionItemChildren itemData={itemData} noDataMessage={noDataMessage} />
            </AccordionItem>
        </Accordion>
    );
};

export default React.memo(SidePanelAccordionItem);
